import { cn } from "@/lib/utils";
import { motion, useReducedMotion } from "framer-motion";
import heroFund from "../../assets/hero-fund.mp4";
import heroGrow from "../../assets/hero-grow.mp4";
import heroMobile from "../../assets/hero-mobile.mp4";
import heroPay from "../../assets/hero-pay.mp4";
import { INTERACTIVE_FOCUS } from "../../lib/chrome";
import { useLandingCopy } from "../../lib/copy";
import { GrainLowOverlay } from "../../lib/grain-low-overlay";
import { GREIGE_GRAIN_SURFACE_CLASS } from "../../lib/greige-grain-surface";
import { LANDING_CONTAINER_CLASS, LANDING_CONTENT_MEASURE_CLASS, LANDING_SECTION_PAD_CLASS } from "../../lib/layout";
import { useLandingLinks } from "../../lib/links";
import {
	HERO_ASSET_HIDDEN,
	HERO_ASSET_SHOWN,
	HERO_CTA_HIDDEN,
	HERO_CTA_SHOWN,
	HERO_SIDE_HIDDEN,
	HERO_SIDE_SHOWN,
	HERO_TITLE_WORDS,
	HERO_WORD_HIDDEN,
	HERO_WORD_SHOWN,
	heroAssetTransition,
	heroCtaTransition,
	heroSideContentTransition,
	heroWordTransition,
} from "./hero-intro-motion";

const HERO_TITLE = HERO_TITLE_WORDS.join(" ");
const HERO_SUBTITLE = "AI powered funding, payments and cash flow management for modern business";

/** Row layout needs ~1080px content (h1 nowrap + gap + subtitle + CTA); assets grid can switch earlier. */
const HERO_COPY_ROW_BP = "min-[1200px]:" as const;

/** Literal tokens — Tailwind v4 cannot emit utilities from prefix+suffix class concatenation. */
const HERO_COPY_ROW_LAYOUT = [
	"min-[1200px]:flex-row",
	"min-[1200px]:items-end",
	"min-[1200px]:justify-between",
	"min-[1200px]:gap-x-10",
] as const;
const HERO_COPY_TITLE_COL = ["min-[1200px]:min-w-0", "min-[1200px]:flex-1", "min-[1200px]:gap-3"] as const;
const HERO_COPY_INLINE = ["min-[1200px]:flex", "min-[1200px]:items-end", "min-[1200px]:gap-8"] as const;

const HERO_ASSETS_BP = "min-[980px]:" as const;
const HERO_ASSETS_SINGLE = ["min-[980px]:hidden"] as const;
const HERO_ASSETS_GRID = ["min-[980px]:grid"] as const;

/** Vertical alignment: bottom below 980px, centred 980–1399px, bottom again at 1400px+. */
const HERO_SECTION_ALIGN = ["justify-end", "min-[980px]:justify-center", "min-[1400px]:justify-end"] as const;
/** Centre band: keep 96px nav floor; lower svh cap so centred copy clears the fixed header. */
const HERO_SECTION_PADDING_TOP = [
	"pt-[clamp(96px,18svh,180px)]",
	"min-[980px]:max-[1399px]:pt-[clamp(96px,10svh,128px)]",
] as const;

const HERO_MOBILE_ASSET = {
	src: heroMobile,
	alt: "Triffin Credit repayment options",
} as const;

const HERO_ASSETS = [
	{ src: heroFund, alt: "Triffin Credit repayment options" },
	{ src: heroPay, alt: "Payment submitted from a phone" },
	{ src: heroGrow, alt: "Multi-currency balances on a laptop" },
] as const;

type THeroAsset = (typeof HERO_ASSETS)[number] | typeof HERO_MOBILE_ASSET;

function HeroVideo({ asset, className }: { asset: THeroAsset; className: string }) {
	return (
		<video
			className={className}
			autoPlay
			loop
			muted
			playsInline
			preload="auto"
			aria-label={asset.alt}
			ref={(el) => {
				// React's muted prop doesn't reliably set the IDL property, and autoplay requires it.
				if (el) el.muted = true;
			}}
		>
			<source src={asset.src} type="video/mp4" />
		</video>
	);
}

/**
 * Section 1 — static hero filling the viewport height.
 * Vertical alignment: bottom below 980px and at 1400px+; centred in the 980–1399px band.
 * Copy row (1200px+): title left, subtitle + CTA inline right.
 * Below 1200px: title, subtitle, and CTA stacked left.
 * Assets: single centred asset below 980px; three-column grid at 980px+.
 */
export function SectionOne() {
	const { getStartedHref } = useLandingLinks();
	const { cta } = useLandingCopy();
	const reducedMotion = useReducedMotion();

	return (
		<section
			className={cn(
				GREIGE_GRAIN_SURFACE_CLASS,
				"z-[1] flex min-h-[100svh] w-full flex-col pb-6 md:pb-10",
				LANDING_SECTION_PAD_CLASS,
				HERO_SECTION_ALIGN,
				HERO_SECTION_PADDING_TOP,
			)}
			aria-label="Triffin"
		>
			<GrainLowOverlay />
			<div className={cn(LANDING_CONTAINER_CLASS, "relative z-[1]")}>
				<div
					className={cn(
						LANDING_CONTENT_MEASURE_CLASS,
						"flex flex-col items-start gap-6 max-[1199px]:gap-5",
						HERO_COPY_ROW_LAYOUT,
					)}
				>
					<div className={cn("flex flex-col items-start gap-5 max-[1199px]:gap-4", HERO_COPY_TITLE_COL)}>
						<h1
							aria-label={HERO_TITLE}
							className="m-0 flex flex-wrap items-baseline gap-x-[0.28em] text-[clamp(44px,8vw,80px)] font-normal leading-[1.05] tracking-[-0.03em] text-teal-500 min-[1200px]:flex-nowrap min-[1200px]:whitespace-nowrap"
						>
							{HERO_TITLE_WORDS.map((word, index) => (
								<span key={word} className="inline-block overflow-hidden pb-[0.06em]">
									<motion.span
										className="inline-block"
										initial={reducedMotion ? false : HERO_WORD_HIDDEN}
										animate={HERO_WORD_SHOWN}
										transition={heroWordTransition(index, Boolean(reducedMotion))}
									>
										{word}
									</motion.span>
								</span>
							))}
						</h1>
						<motion.p
							className="m-0 max-w-[min(100%,480px)] text-landing-section-body font-normal text-teal-500 min-[1200px]:hidden"
							initial={reducedMotion ? false : HERO_SIDE_HIDDEN}
							animate={HERO_SIDE_SHOWN}
							transition={heroSideContentTransition(Boolean(reducedMotion))}
						>
							{HERO_SUBTITLE}
						</motion.p>
					</div>
					<div className={cn("contents", HERO_COPY_INLINE)}>
						<motion.p
							className="m-0 hidden max-w-[307px] text-landing-section-body font-normal text-teal-500 min-[1200px]:block"
							initial={reducedMotion ? false : HERO_SIDE_HIDDEN}
							animate={HERO_SIDE_SHOWN}
							transition={heroSideContentTransition(Boolean(reducedMotion))}
						>
							{HERO_SUBTITLE}
						</motion.p>
						<motion.div
							className="min-[1200px]:self-end"
							initial={reducedMotion ? false : HERO_CTA_HIDDEN}
							animate={HERO_CTA_SHOWN}
							transition={heroCtaTransition(Boolean(reducedMotion))}
						>
							<a
								className={cn(
									"inline-flex h-[46px] w-[156px] shrink-0 items-center justify-center px-6 text-sm font-normal leading-[1.3] tracking-[-0.02em] whitespace-nowrap no-underline",
									"bg-green-500 text-teal-600 hover:bg-green-400 focus-visible:bg-green-400",
									INTERACTIVE_FOCUS,
								)}
								href={getStartedHref}
							>
								{cta.getStartedLabel}
							</a>
						</motion.div>
					</div>
				</div>

				{/* Below 980px: single centred asset. 980px+: three assets in a row, videos covering their blocks. */}
				<div className="mt-12 md:mt-16">
					<div className={cn("flex justify-center", HERO_ASSETS_SINGLE)}>
						<motion.div
							className="w-full"
							initial={reducedMotion ? false : HERO_ASSET_HIDDEN}
							animate={HERO_ASSET_SHOWN}
							transition={heroAssetTransition(2, Boolean(reducedMotion))}
						>
							<HeroVideo asset={HERO_MOBILE_ASSET} className="block h-auto w-full select-none" />
						</motion.div>
					</div>
					<ul className={cn("m-0 hidden list-none grid-cols-3 gap-2.5 p-0", HERO_ASSETS_GRID)}>
						{HERO_ASSETS.map((asset, index) => (
							<li key={asset.src} className="aspect-[453/560] overflow-hidden">
								<motion.div
									className="h-full w-full"
									initial={reducedMotion ? false : HERO_ASSET_HIDDEN}
									animate={HERO_ASSET_SHOWN}
									transition={heroAssetTransition(index, Boolean(reducedMotion))}
								>
									<HeroVideo asset={asset} className="block h-full w-full select-none object-cover" />
								</motion.div>
							</li>
						))}
					</ul>
				</div>
			</div>
		</section>
	);
}
